import {getAudioList} from './CHELAudio';
import {hel_show_vr_keyboard} from './heliodor_xr';
import {
  ETextParamType_ScriptCallCanvasComponentMethod,
  ETextParamType_ScriptCallComponentMethod
} from './logics/const';
import {
  getActionProcedureList,
  getOnclickedAvatarProcedureList,
  getOnloadedProcedureList,
  getOnfirsttouchProcedureList,
  getOnresizeProcedureList,
  getSkywayCommandProcedureList,
  getSkywayAvatarNumberForSend,
  getSkywayUseCustomAvatarIcon,
  getSkywayName,
  getCurrentUserName,
  getTextureCanvasList,
  getWorldId,
  getVCUID,
  setActionProcedureList,
  setOnclickedAvatarProcedureList,
  setOnfirsttouchProcedureList,
  setOnresizeProcedureList,
  setSkywayCommandProcedureList,
  setSkywayMyinfoProcedureList,
  setSkywayAvatarNumber,
  setSkywayAvatarNumberForSend,
  setSkywayUseCustomAvatarIcon,
  setSkywayCustomAvatarIconURL,
  setSkywayName,
  setTextureCanvasList,
  setUploadedFileBuffer
} from './logics/dataOps';
import {
  defineNextTextCanvasIndex,
  getConfigUrlDOMElement,
  getLocalTextConfigUrlDOMElement,
  getLocalTextUserNameDOMElement,
  getWebGLRenderingContext,
  getInputFileDOMElement,
  getUserNameDOMElement,
  getSkywayMsgDOMElement,
  getSkywayLocalTextDOMElement
} from './logics/domOps';
import {getMainCanvasDOMElement} from './logics/mainCanvasDOM';
import {hel_remove_emoji} from './logics/misc';
import {hel_mixer_SetVolume} from './logics/mixerOps';
import {
  hel_DispatchEvent,
  hel_postMessage,
  hel_setText,
  requestMemoryAllocate,
  requestOnDropFiles,
  requestOnDropFilesBegin,
  requestOnDropFilesProgress,
  requestProcedureCallBinaryFor,
  requestReleaseMemoryFor,
  requestSetDataToHeap
} from './logics/moduleCaller';
import {hel_isLangJa, hel_isMobile, hel_isVRDevice} from './logics/platform';
import {
  hel_set_cookie,
  hel_get_cookie,
  hel_gtag,
  hel_set_show_TextField_configurl,
  hel_set_show_TextField_UserName,
  hel_post_TextField_UserName,
  hel_skyway_set_show_chatbox,
  hel_onloaded_add,
  hel_skyway_myinfo_bridge_call,
  hel_toast_notify_message,
  hel_send_notify_message,
  hel_textchat_notify_message,
  hel_textchat_send_message,
  hel_vcc_notify_removed
} from './logics/shared';
import {hel_skyway_send_data, hel_skyway_send_myinfo} from './logics/skyway';

import type {
  ProcedureCallFunc,
  ProcedureOnclickedAvatarCallFunc,
  ProcedureOnloadedCallFunc,
  ProcedureOnfirsttouchCallFunc,
  ProcedureOnresizeCallFunc,
  ProcedureSkywayCommandCallFunc,
  ProcedureSkywayMyinfoCallFunc
} from './types/internal';

declare global {
  /* eslint-disable no-var -- NOTE: TypeScriptのグローバルスコープ拡張では「var」が必須 */
  var hel_onclickedavatar: ProcedureOnclickedAvatarCallFunc | undefined;
  var hel_onloaded: ProcedureOnloadedCallFunc | undefined;
  var hel_onbeginloading: (() => void) | undefined;
  var hel_action_bridge: ProcedureCallFunc | undefined;
  /* eslint-enable */
}

/**
 * @param Type
 * @param ary_u8
 */
const hel_setBinary = (Type: number, ary_u8: Uint8Array): number => {
  return requestProcedureCallBinaryFor(Type, ary_u8);
};

/**
 * @param index
 */
const getTextCanvas = (index: number): HTMLCanvasElement | null => {
  return getTextureCanvasList()[index] ?? null;
};

const setFileReaderEvents = (files: any[]) => {
  if (files.length <= 0) {
    return;
  }

  const file = files[0];

  requestOnDropFilesBegin(file);

  //
  const reader = new FileReader();

  reader.onprogress = (evt: ProgressEvent<FileReader>) => {
    if (!evt.lengthComputable || evt.total <= 0) {
      return;
    }
    requestOnDropFilesProgress(evt);
  };

  reader.onload = () => {
    setUploadedFileBuffer(reader.result);

    const ary_u8 = new Uint8Array(reader.result as ArrayBuffer);

    // console.debug("onload " + file.name + " / " + ary_u8.length);
    const data = requestMemoryAllocate(ary_u8.length);
    requestSetDataToHeap(ary_u8, data);
    requestOnDropFiles(file, ary_u8, data);
    requestReleaseMemoryFor(data);
  };

  reader.readAsArrayBuffer(file);
};

const helcore_TransDevicePixelRatio = (n: number): number => {
  return Math.floor(n / window.devicePixelRatio);
};

Object.assign(globalThis, {
  // Cookie
  /**
   * @remarks
   * heliodor, skyway, libからもC++からも参照されてる
   *
   * heliodor.js, heliodor_skyway, C++側とShare関数
   */
  hel_set_cookie,

  /**
   * @remarks
   * heliodor, skyway, xr, libからもC++からも参照されてる
   *
   * heliodor.js, heliodor_skyway, C++側とShare関数
   */
  hel_get_cookie,

  /**
   * HeliScriptから参照
   * @param name
   * @returns
   */
  hel_get_cookie_onEmptyStr: (name: string): string => {
    const matches = document.cookie.match(
      // eslint-disable-next-line no-useless-escape
      new RegExp('(?:^|; )' + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)')
    );

    return matches?.[1] ? decodeURIComponent(matches[1]) : '';
  },

  /**
   * @remarks
   * heliodor_skyway, xrからもC++からも参照されてる
   *
   * heliodor.js, heliodor_skyway, C++側とShare関数
   */
  hel_setText,

  //
  hel_postMessage,

  hel_setBinary,

  /**
   * @remarks
   * GUIのItemNameに依存した関数
   * itemName/componentName/methodName/param
   * @version 2.9.0
   * @param itemName
   * @param componentName
   * @param methodName
   * @param param
   */
  hel_script_CallComponentMethod: (
    itemName: string,
    componentName: string,
    methodName: string,
    param: string
  ) => {
    hel_setText(
      ETextParamType_ScriptCallComponentMethod,
      itemName + ',' + componentName + ',' + methodName + ',' + param
    );
  },

  /**
   * HeliScriptのコンポーネント呼び出し
   * @remarks
   * 本関数をTypeScript層内における呼び出し用に転用することは禁止。代わりに {@link hel_DispatchEvent} を利用する。
   */
  hel_script_CallCanvasComponentMethod: (
    layerName: string,
    componentName: string,
    methodName: string,
    param: string
  ) => {
    hel_setText(
      ETextParamType_ScriptCallCanvasComponentMethod,
      layerName + ',' + componentName + ',' + methodName + ',' + param
    );
  },

  /**
   * heliodor.jsからもC++からも参照されてる
   * @param index
   */
  getPlayTimeAudio: (index: number): number => {
    const audioList = getAudioList();
    if (audioList[index] === null) {
      return 0;
    }

    return audioList[index]?.getPlayTimeAudio() || 0;
  },

  /**
   * @remarks
   * heliodor.jsからもC++からも参照されてる
   */
  hel_mixer_SetVolume,

  // MasterSound
  hel_mixer_SetMasterVolume: (volume: number) => {
    hel_set_cookie('mixervolume_master', volume as unknown as string);
  },

  hel_mixer_SetMuteMaster: (mute: number) => {
    hel_set_cookie('mixermute_master', mute as unknown as string);
  },

  // Texture
  /**
   * heliodor.jsからもC++からも参照されてる
   * @param size
   */
  hel_texture_IsPower2Size: (size: number): boolean => {
    return (size & (size - 1)) === 0;
  },

  // Texture Canvas

  /**
   * heliodor.jsからもC++からも参照されてる
   * @param width
   * @param height
   */
  createTextCanvas: (width: number, height: number) => {
    const index = defineNextTextCanvasIndex();

    const textCanvas = document.createElement('canvas');

    setTextureCanvasList(textCanvas, index);

    textCanvas.style.visibility = 'hidden';
    textCanvas.width = width;
    textCanvas.height = height;

    return index;
  },

  /**
   * @remarks
   * heliodor.jsからもC++からも参照されてる
   */
  getTextCanvas,

  /**
   * heliodor.jsからもC++からも参照されてる
   * @param index
   */
  getTextCanvasContext: (index: number) => {
    const textCanvas = getTextCanvas(index);
    if (textCanvas === null) return null;

    return textCanvas.getContext('2d');
  },

  /**
   * heliodor.jsからもC++からも参照されてる
   * @param index
   */
  clearTextCanvas: (index: number) => {
    const textCanvas = getTextCanvas(index);
    if (textCanvas === null) return;

    const context = textCanvas.getContext('2d');
    if (context === null) return;

    context.clearRect(0, 0, textCanvas.width, textCanvas.height);
  },

  /**
   * heliodor.jsからもC++からも参照されてる
   * @param index
   */
  releaseTextCanvasIndex: (index: number) => {
    const canvasList = getTextureCanvasList();
    if (index < 0 || index >= canvasList.length || canvasList[index] === null) {
      return;
    }
    setTextureCanvasList(null, index);
  },

  // TAG
  /**
   * @remarks
   * heliodor.js, skywayからもC++からも参照されてる
   */
  hel_gtag,

  // File Drag & Drop

  /**
   * C++から参照
   */
  hel_get_MainWebGLRenderingContext: () => {
    return getWebGLRenderingContext();
  },

  /**
   * heliodor.jsからもC++からも参照されてる
   */
  hel_beginDropFiles: () => {
    const mainCanvas = getMainCanvasDOMElement();
    if (mainCanvas === undefined) {
      return;
    }
    mainCanvas.addEventListener(
      'dragover',
      (e: Event) => {
        e.stopPropagation();
        e.preventDefault();
      },
      false
    );

    mainCanvas.addEventListener(
      'dragleave',
      (e: Event) => {
        e.stopPropagation();
        e.preventDefault();
      },
      false
    );

    mainCanvas.addEventListener(
      'drop',
      (e: any) => {
        e.stopPropagation();
        e.preventDefault();
        setFileReaderEvents(e.dataTransfer!.files || []);
      },
      false
    );

    const inputFile = getInputFileDOMElement();
    if (inputFile !== undefined) {
      inputFile.addEventListener(
        'change',
        (e: any) => {
          e.stopPropagation();
          e.preventDefault();
          // @ts-ignore
          // eslint-disable-next-line no-invalid-this
          const files = this.files!;
          setFileReaderEvents(files || []);
        },
        false
      );
    }
  },

  //

  /**
   * 汎用的なインプットフィールドのリサイズ関数
   */
  hel_resize_InputField: (
    x: number,
    y: number,
    width: number,
    height: number,
    fontSize: number,
    DivElementName: string,
    InputElementName: string
  ) => {
    x = helcore_TransDevicePixelRatio(x);
    y = helcore_TransDevicePixelRatio(y);
    width = helcore_TransDevicePixelRatio(width);
    height = helcore_TransDevicePixelRatio(height);
    fontSize = helcore_TransDevicePixelRatio(fontSize);

    //
    const DivElement = document.getElementById(DivElementName);
    if (DivElement !== undefined && DivElement !== null) {
      DivElement.style.left = x + 'px';
      DivElement.style.top = y + 'px';
      DivElement.style.width = width + 'px';
      DivElement.style.height = height + 'px';
      const mainCanvas = getMainCanvasDOMElement();
      if (mainCanvas !== undefined) {
        const clientwidth = Math.floor(mainCanvas.clientWidth * window.devicePixelRatio);
        const clientheight = Math.floor(mainCanvas.clientHeight * window.devicePixelRatio);
        let Scale = clientwidth / 1280;
        // 横画面
        if (clientwidth >= clientheight) {
          const Height = 720 * Scale;
          if (Height > clientheight) {
            Scale = clientheight / 720;
          }
        }
        // 縦画面
        else {
          const Height = (clientwidth * 1280) / 720;
          if (Height > clientheight) {
            Scale = (clientheight * 720) / (1280 * 1280);
          }
        }
        DivElement.style.transform = `scale(${Scale})`;
        DivElement.style.transformOrigin = 'top left';
      }
    }

    //
    const InputElement = document.getElementById(InputElementName);
    if (InputElement !== undefined && InputElement !== null) {
      InputElement.style.width = width + 'px';
      InputElement.style.height = height + 'px';
      InputElement.style.fontSize = fontSize + 'px';
    }
  },

  /**
   *
   * HeliScriptから参照
   */
  hel_set_HTMLElement_visibility: (elementName: string, show: boolean) => {
    const element = document.getElementById(elementName);
    if (element !== undefined && element !== null) {
      element.style.visibility = show ? 'visible' : 'hidden';
    }
  },

  // configurl
  /**
   * @remarks
   * heliodor.jsからもC++からも参照されてる
   */
  hel_set_show_TextField_configurl,

  /**
   * heliodor.jsからもC++からも参照されてる
   * @param x
   * @param y
   * @param width
   * @param height
   */
  hel_resize_configurl: (x: number, y: number, width: number, height: number) => {
    x = helcore_TransDevicePixelRatio(x);
    y = helcore_TransDevicePixelRatio(y);
    width = helcore_TransDevicePixelRatio(width);
    height = helcore_TransDevicePixelRatio(height);

    const configUrl = getConfigUrlDOMElement();
    if (configUrl !== undefined) {
      configUrl.style.left = x + 'px';
      configUrl.style.top = y + 'px';
    }

    const localTextConfigUrl = getLocalTextConfigUrlDOMElement();
    if (localTextConfigUrl !== undefined) {
      localTextConfigUrl.style.width = width + 'px';
      localTextConfigUrl.style.height = height + 'px';
    }
  },

  /**
   * heliodor.jsからもC++からも参照されてる
   * @param text
   */
  hel_set_TextField_configurl: (text: string) => {
    const localTextConfigUrl = getLocalTextConfigUrlDOMElement();
    if (localTextConfigUrl !== undefined) {
      (localTextConfigUrl as any).value = text;
    }
  },

  /**
   * heliodor.jsからもC++からも参照されてる
   * @param text
   */
  hel_set_TextField_configurl_fontSize: (fontSize: number) => {
    fontSize = helcore_TransDevicePixelRatio(fontSize);

    const localTextConfigUrl = getLocalTextConfigUrlDOMElement();
    if (localTextConfigUrl !== undefined) {
      localTextConfigUrl.style.fontSize = fontSize + 'px';
    }
  },

  // username
  /**
   * @remarks
   * heliodor.jsからもC++からも参照されてる
   */
  hel_set_show_TextField_UserName,

  /**
   * heliodor.jsからもC++からも参照されてる
   * @param name
   */
  hel_set_TextField_UserName: (name: string) => {
    // console.debug("*** A-END");
    const localTextUserName = getLocalTextUserNameDOMElement();
    // nameが 'undefined' ＝文字列で入ってる場合がある
    if (localTextUserName === undefined || name === undefined) {
      return;
    }
    (localTextUserName as any).value = name;

    // console.debug("*** A-globalThis.g_localText_username(" + globalThis.g_localText_username + ")");
    // console.debug("*** A-globalThis.g_localText_username.value(" + globalThis.g_localText_username.value + ")");
    // console.debug("*** A-name(" + name + ")");
    // console.debug("*** A-END");
  },

  /**
   * @remarks
   * heliodor.jsからもC++からも参照されてる
   */
  hel_post_TextField_UserName,

  /**
   * heliodor.jsからもC++からも参照されてる
   * @param text
   */
  hel_set_TextField_UserName_fontSize: (fontSize: number) => {
    fontSize = helcore_TransDevicePixelRatio(fontSize);

    const localTextUserName = getLocalTextUserNameDOMElement();
    if (localTextUserName !== undefined) {
      localTextUserName.style.fontSize = fontSize + 'px';
    }
  },

  /**
   * heliodor.jsからもC++からも参照されてる
   */
  hel_update_TextField_UserName: () => {
    const localTextUserName = getLocalTextUserNameDOMElement();
    if (localTextUserName === undefined) return;

    const skywayName = getSkywayName();

    hel_remove_emoji(localTextUserName);
    const value = (localTextUserName as any).value;
    if (skywayName !== value) {
      setSkywayName(value);

      hel_set_cookie('playername', value);
    }

    hel_skyway_send_myinfo();
  },

  /**
   * heliodor.jsからもC++からも参照されてる
   * @param x
   * @param y
   * @param width
   * @param height
   */
  hel_resize_UserName: (x: number, y: number, width: number, height: number) => {
    x = helcore_TransDevicePixelRatio(x);
    y = helcore_TransDevicePixelRatio(y);
    width = helcore_TransDevicePixelRatio(width);
    height = helcore_TransDevicePixelRatio(height);

    const userName = getUserNameDOMElement();
    if (userName !== undefined) {
      userName.style.left = x + 'px';
      userName.style.top = y + 'px';
    }

    const localTextUserName = getLocalTextUserNameDOMElement();
    if (localTextUserName !== undefined) {
      localTextUserName.style.width = width + 'px';
      localTextUserName.style.height = height + 'px';
    }
  },

  /**
   * CurrentUserName(GET)
   */
  hel_get_current_username: () => {
    return getCurrentUserName();
  },

  /**
   * SkywayName(GET)
   */
  hel_get_skyway_username: () => {
    return getSkywayName();
  },

  /**
   * @remarks
   * heliodor.js, heliodor_skyway_rcp.js, heliodor_skyway_xrw.js, C++から参照あり
   */
  hel_skyway_set_show_chatbox,

  /**
   * heliodor.js, heliodor_skyway_rcp.js, heliodor_skyway_xrw.js, C++から参照あり
   * @param text
   */
  hel_skyway_set_TextField_localText_fontSize: (fontSize: number) => {
    fontSize = helcore_TransDevicePixelRatio(fontSize);

    const skywayLocalText = getSkywayLocalTextDOMElement();
    if (skywayLocalText === undefined) {
      return;
    }

    skywayLocalText.style.fontSize = fontSize + 'px';
  },

  /**
   * heliodor.js, heliodor_skyway_rcp.js, heliodor_skyway_xrw.js, C++から参照あり
   * @param x
   * @param y
   * @param width
   * @param height
   */
  hel_skyway_resize_chatbox: (x: number, y: number, width: number, height: number) => {
    const msg = getSkywayMsgDOMElement();
    if (msg === undefined) {
      return;
    }

    x = helcore_TransDevicePixelRatio(x);
    y = helcore_TransDevicePixelRatio(y);
    width = helcore_TransDevicePixelRatio(width);
    height = helcore_TransDevicePixelRatio(height);

    msg.style.left = x + 'px';
    msg.style.top = y + 'px';

    const skywayLocalText = getSkywayLocalTextDOMElement();
    if (skywayLocalText === undefined) {
      return;
    }
    skywayLocalText.style.width = width + 'px';
    skywayLocalText.style.height = height + 'px';
  },

  /**
   * JSから参照あり
   */
  hel_skyway_send_JSCustomData: (param: string) => {
    hel_skyway_send_data('jsdata:' + param);
  },

  /**
   * heliodor.js, heliodor_skyway_rcp.js, heliodor_skyway_xrw.js, C++から参照あり
   * @param avatar
   */
  hel_skyway_set_avatar_forsend: (avatar: number) => {
    setSkywayAvatarNumberForSend(avatar);
  },

  hel_skyway_set_avatar: (avatar: number) => setSkywayAvatarNumber(avatar),

  // Avatar
  /**
   * heliodor.js, heliodor_skyway_rcp.js, heliodor_skyway_xrw.js, C++から参照あり
   * @param avatar
   */
  hel_skyway_change_avatar: (avatar: number, uuid: string, setcookie: boolean) => {
    setSkywayAvatarNumber(avatar);
    setSkywayAvatarNumberForSend(avatar);

    hel_gtag('AvatarChange', {param0: getWorldId(), param1: avatar} as any);

    if (setcookie) {
      hel_set_cookie('Avatar', avatar as unknown as string);
      hel_set_cookie('MyAvatarUUID', uuid as unknown as string);
    }
  },

  hel_skyway_get_avatar: () => {
    return getSkywayAvatarNumberForSend();
  },

  /**
   * AvatarIcon
   */
  hel_set_custom_avataricon: (url: string | null) => {
    setSkywayUseCustomAvatarIcon(url !== null && url !== '');
    setSkywayCustomAvatarIconURL(url ?? '');
  },

  hel_get_use_custom_avataricon: () => {
    return getSkywayUseCustomAvatarIcon() ? 1 : 0;
  },

  /**
   * マイク切り替えはCanvas層が担うので何もしない。
   * @remarks C++層から参照あり。
   * @deprecated C++層から参照するコードを削除すると同時に本グローバル関数も削除する。
   * @see https://github.com/VRHIKKY/WorldViewer/blob/1f877fb179acefcbbf003c071095ddd58a4b68b2/Heliodor/Source/App/CEmscriptenEvent.cpp#L835
   * @see https://discord.com/channels/596169759045976094/1150648243387125790/1384789458540302426
   */
  hel_skyway_set_mic_state: () => {},

  /**
   * heliodor.js, JS, C++ から参照あり
   */
  hel_onclickedavatar_call: (playerID: string) => {
    for (const procedure of getOnclickedAvatarProcedureList()) {
      // TODO: 型が正しければ if は不要なはず
      if (typeof procedure === 'function') {
        procedure(playerID);
      }
    }

    if (typeof globalThis.hel_onclickedavatar === 'function') {
      globalThis.hel_onclickedavatar(playerID);
    }
  },

  /**
   * heliodor.js, JS, C++ から参照あり
   */
  hel_onclickedavatar_add: (callFunc: ProcedureOnclickedAvatarCallFunc) => {
    setOnclickedAvatarProcedureList(callFunc);
  },

  /**
   * heliodor.js, JS、C++から参照あり
   */
  hel_onloaded_call: () => {
    for (const procedure of getOnloadedProcedureList()) {
      // TODO: 型が正しければ if は不要なはず
      if (typeof procedure === 'function') {
        procedure();
      }
    }

    if (typeof globalThis.hel_onloaded === 'function') {
      globalThis.hel_onloaded();
    }
  },

  /**
   * heliodor.js, JS、C++から参照あり
   */
  hel_onbeginloading_call: () => {
    if (typeof globalThis.hel_onbeginloading === 'function') {
      globalThis.hel_onbeginloading();
    }
  },

  /**
   * @remarks
   * heliodor.js, JS、C++から参照あり
   */
  hel_onloaded_add,

  /**
   * heliodor.js, JS、C++から参照あり
   */
  hel_onfirsttouch_call: () => {
    for (const procedure of getOnfirsttouchProcedureList()) {
      // TODO: 型が正しければ if は不要なはず
      if (typeof procedure === 'function') {
        procedure();
      }
    }
  },

  /**
   * heliodor.js, JS、C++から参照あり
   */
  hel_onfirsttouch_add: (callFunc: ProcedureOnfirsttouchCallFunc) => {
    if (typeof callFunc !== 'function') {
      return;
    }
    setOnfirsttouchProcedureList(callFunc);
  },

  /**
   * heliodor.js, JS、C++から参照あり
   */
  hel_onresize_generic_call: () => {
    for (const procedure of getOnresizeProcedureList()) {
      // TODO: 型が正しければ if は不要なはず
      if (typeof procedure === 'function') {
        procedure();
      }
    }
  },

  /**
   * heliodor.js, JS、C++から参照あり
   */
  hel_onresize_generic_add: (callFunc: ProcedureOnresizeCallFunc) => {
    setOnresizeProcedureList(callFunc);
  },

  /**
   * heliodor.js, JS、C++から参照あり
   * @remarks
   * 関数をセット
   * @param actionProcedureCall
   * @version ^3.4
   */
  hel_action_bridge_add: (actionProcedureCall: ProcedureCallFunc) =>
    setActionProcedureList(actionProcedureCall),

  /**
   * @remarks
   * C++から呼び出しのあるプロシージャコール型ブリッジ
   * 現在ご完成を考えhel_action_bridgeも連携する（heliodor_front.jsを利用しても可）
   * @param name
   * @param text
   * @version ^3.4
   */
  hel_action_bridge_call: (name: string, text: string | number) => {
    const procedures = getActionProcedureList();
    for (let i = 0; i < procedures.length; i += 1) {
      if (procedures[i]?.(name, text)) {
        return;
      }
    }
    if (typeof globalThis.hel_action_bridge === 'function') {
      globalThis.hel_action_bridge(name, text);
    }
  },

  /**
   * JSから参照あり
   * JSからSkywayCommandをハンドリングする
   */
  hel_skyway_command_bridge_add: (callFunc: ProcedureSkywayCommandCallFunc) => {
    setSkywayCommandProcedureList(callFunc);
  },

  /**
   * JSから参照あり
   * JSからSkywayCommandをハンドリングする
   */
  hel_skyway_command_bridge_call: (ID: string, Type: string, Data: string) => {
    for (const procedure of getSkywayCommandProcedureList()) {
      // TODO: 型が正しければ if は不要なはず
      if (typeof procedure === 'function') {
        procedure(ID, Type, Data);
      }
    }
  },

  /**
   * JSから参照あり
   * カスタムユーザーデータを送信する
   */
  hel_skyway_myinfo_bridge_add: (callFunc: ProcedureSkywayMyinfoCallFunc) => {
    setSkywayMyinfoProcedureList(callFunc);
  },

  /**
   * @remarks
   * JSから参照あり
   */
  hel_skyway_myinfo_bridge_call,

  /**
   * JS, HeliScriptから参照あり
   */
  hel_isLangJa,
  hel_isMobile,
  hel_isVRDevice,

  // 正規表現(大文字小文字の判別)
  /**
   * HeliScriptから参照あり
   */
  hel_regex_is_halfWidth: (c: number): boolean => {
    const cStr = String.fromCharCode(c);

    if (cStr.match(/[ -~]/)) {
      return true;
    } else {
      return false;
    }
  },

  /**
   * HeliScriptから参照あり
   */
  hel_get_vcuid: (): string => {
    const vcuid = getVCUID();

    if (vcuid === undefined || vcuid === null) {
      return '';
    }

    return vcuid;
  },

  /**
   * @remarks
   * JS, HeliScriptから参照あり
   */
  hel_toast_notify_message,

  /**
   * @remarks
   * JS, HeliScriptから参照あり
   */

  hel_send_notify_message,

  /**
   * @remarks
   * JS, HeliScriptから参照あり
   */
  hel_textchat_notify_message,

  /**
   * @remarks
   * JS, HeliScriptから参照あり
   */
  hel_textchat_send_message,

  /**
   * HeliScriptのチャンネルセッションAPIで参照
   */
  hel_get_worldid: () => {
    return getWorldId();
  },

  /**
   * @remarks
   * JSから参照有り
   */
  hel_vcc_notify_removed,

  // アクティビティから参照
  hel_ui_open_enterChannelDialog: (param: object) => {
    hel_DispatchEvent('ui-open-enter-channel-dialog', JSON.stringify(param));
  },

  // アクティビティから参照
  hel_ui_close_enterChannelDialog: () => {
    hel_DispatchEvent('ui-close-enter-channel-dialog', '');
  },

  // アクティビティから参照
  hel_ui_enter_voicechannel: (channelID: string) => {
    hel_DispatchEvent('ui-enter-voice-channel', channelID);
  },

  /**
   * VRの仮想キーボードを表示する
   */
  hel_show_vr_keyboard,

  /*
   * フォーカスを解除する
   * HeliScriptから参照あり
   */
  hel_clear_focus: () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  },

  /*
   * テキストチャットがフォーカスされているかどうか
   * HeliScriptから参照あり
   */
  hel_is_textchat_focused: (): boolean => {
    return document.getElementById('textchat')?.matches(':focus-within') ?? false;
  }
});
